/**
 * NameTypeahead — iter303aj-fix4
 * ------------------------------
 * Runner-form input for name-flavour fields. Wraps the shared Combobox
 * with a debounced async fetch against `/api/name-suggestions`. Operators
 * can pick from the suggestion list OR type any new value (allowCustom).
 *
 * Suggestions are frequency-ranked from the operational collections
 * (clients / properties / agents) so top-of-list is naturally the most-
 * common values in the business today. As data grows the list tightens.
 *
 * Props:
 *   value              string  — the current value
 *   onChange           fn(v)   — called on pick or blur with a custom value
 *   suggestionsSource  string  — the `field` param on the backend endpoint
 *                                (first_name | last_name | company_name |
 *                                tenant_contact_name | agent_name | agent_company)
 *   placeholder        string  — displayed when empty
 *   testId             string  — data-testid for the outer wrapper
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Combobox from '@/components/Combobox';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function NameTypeahead({ value, onChange, suggestionsSource,
                                        placeholder = '', testId = 'name-typeahead' }) {
  const [options, setOptions] = useState([]);
  const debounceRef = useRef(null);

  const fetchOptions = useCallback(async (q) => {
    try {
      const { data } = await api.get('/name-suggestions', {
        params: { field: suggestionsSource, q: q || '', limit: 20 },
      });
      setOptions(Array.isArray(data?.options) ? data.options : []);
    } catch {
      setOptions([]);
    }
  }, [suggestionsSource]);

  // Initial fetch (top-N common values with empty query) once source is set.
  useEffect(() => {
    if (suggestionsSource) fetchOptions('');
  }, [suggestionsSource, fetchOptions]);

  // Debounced re-fetch as the user types (Combobox drives `query` internally
  // — we mirror via onChange only when a pick or custom-commit lands, so we
  // hook into onChange with a wrapper that also triggers a fresh fetch).
  const handleChange = (v) => {
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(v), 200);
  };

  return (
    <Combobox
      value={value || ''}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      testId={testId}
      allowCustom
    />
  );
}
